import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu, X, LifeBuoy, LogIn, ArrowRight, Gift, Sparkles, Tag, Compass,
  ChevronDown, ChevronLeft, ChevronRight, HelpCircle, Mail, FileText, BookOpen, Map,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import JunagoLogo from "./JunagoLogo";
import { isLandingMode } from "../lib/landingMode";
import { FEATURE_PAGES } from "../data/featurePages";
import { SOLUTION_PAGES } from "../data/solutionPages";
import { openInteractiveDemo } from "./demo/DemoLauncher";

const IMG_SOLUTIONS = "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/66ea0476ffedfcb747b5f758e524c2c8ac4e5b7b5c554de2bc40352bc6cb1394.png";
const IMG_PRICING = "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/b57772a3517d8ca6a9dd9f3100b18ebecd8d5bb7073c42e0db1273d7e6589490.png";
const IMG_RESOURCES = "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/eda72bed7ec330635a37b6c4ae7b9e1f7b634eb695b044cf3717bb7a9f2dc20b.png";

const RESOURCE_ARTICLES = [
  { slug: "prise-en-main-pilotage", label: "Prendre en main le Pilotage" },
  { slug: "cycle-de-vie-reservation", label: "Du devis à la restitution" },
  { slug: "contrats-signature-electronique", label: "Contrats & signature électronique" },
  { slug: "junago-copilot-assistant-ia", label: "Junago Copilot : l'assistant IA" },
];

// ─── Building blocks ───

function MenuLink({ icon: Icon, label, sub, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-100/80 transition-colors text-left group"
      data-testid={testid}
    >
      <Icon className="h-4 w-4 text-violet-600 shrink-0 mt-0.5" strokeWidth={1.75} />
      <span>
        <span className="block text-sm font-medium text-slate-800 group-hover:text-slate-900">{label}</span>
        {sub && <span className="block text-xs text-slate-500 mt-0.5">{sub}</span>}
      </span>
    </button>
  );
}

function VisualCard({ go, to, onPress, img, design, overline, title, testid }) {
  return (
    <button onClick={() => (onPress ? onPress() : go(to))} className="group relative block w-full sm:w-72 lg:w-80 h-full min-h-[220px] rounded-xl overflow-hidden text-left" data-testid={testid}>
      {img ? (
        <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
      ) : (
        design
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/25 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="text-[9px] tracking-[0.2em] uppercase font-bold text-violet-300">{overline}</div>
        <div className="mt-1 text-sm font-semibold text-white leading-snug">{title}</div>
        <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-white/90">
          Découvrir <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </button>
  );
}

// ─── Mega menu panels ───

function SolutionsPanel({ go, demo }) {
  return (
    <div className="flex flex-col sm:flex-row">
      <div className="p-5 md:p-6 flex-1">
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-violet-600 px-3 mb-3">Junago répond à vos enjeux</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          {SOLUTION_PAGES.map((s) => (
            <MenuLink
              key={s.slug}
              icon={s.icon}
              label={s.menuLabel}
              sub={s.menuSub}
              onClick={() => go(`/solutions/${s.slug}`)}
              testid={`mega-solution-${s.slug}`}
            />
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 px-3">
          <button
            onClick={() => go("/solutions")}
            className="btn-jn-secondary inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
            data-testid="mega-solutions-all"
          >
            Voir toutes les solutions <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div className="p-3 sm:pl-0 flex">
        <VisualCard
          go={go}
          onPress={demo}
          img={IMG_SOLUTIONS}
          overline="À essayer"
          title="La démo interactive, sans créer de compte."
          testid="mega-solution-visual"
        />
      </div>
    </div>
  );
}

const FEATURE_COLUMNS = [
  { heading: "Opérations", slugs: ["pilotage-calendrier", "reservations-devis", "crm-clients", "etats-des-lieux"] },
  { heading: "Ventes & finances", slugs: ["produits-options", "contrats-signature", "encaissements", "revenus-facturation", "emails-automatiques"] },
  { heading: "Plateforme", slugs: ["assistant-ia", "page-de-reservation", "equipe-securite", "parametrages", "flotte-entretien"] },
];

function FeaturesPanel({ go }) {
  const bySlug = Object.fromEntries(FEATURE_PAGES.map((f) => [f.slug, f]));
  return (
    <div className="flex flex-col sm:flex-row">
      <div className="p-5 md:p-6 flex-1">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
          {FEATURE_COLUMNS.map((col) => (
            <div key={col.heading}>
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-violet-600 px-3 mb-2">{col.heading}</div>
              {col.slugs.map((slug) => {
                const f = bySlug[slug];
                if (!f) return null;
                return (
                  <button
                    key={slug}
                    onClick={() => go(`/fonctionnalites/${slug}`)}
                    className="group w-full flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-slate-100/80 transition-colors text-left"
                    data-testid={`mega-feature-${slug}`}
                  >
                    <f.icon className="h-4 w-4 text-violet-600 shrink-0 transition-transform duration-200 group-hover:scale-110" strokeWidth={1.75} />
                    <span className="text-[13px] font-medium text-slate-800 group-hover:text-slate-900">{f.menuLabel}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 px-3">
          <button
            onClick={() => go("/fonctionnalites")}
            className="btn-jn-secondary inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
            data-testid="mega-features-all"
          >
            Voir toutes les fonctionnalités <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div className="p-3 sm:pl-0 flex">
        <button
          onClick={() => go("/fonctionnalites/assistant-ia")}
          className="group relative block w-full sm:w-60 rounded-xl overflow-hidden text-left border border-slate-200 bg-white transition-all duration-300 hover:border-violet-300 hover:shadow-[0_16px_40px_-16px_rgba(124,58,237,0.4)]"
          data-testid="mega-features-visual"
        >
          <div className="relative h-28 overflow-hidden">
            <img src="/captures/copilot.jpg" alt="" className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.05]" />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
            <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-600 text-white text-[9px] font-bold tracking-[0.15em] uppercase">
              <Sparkles className="h-2.5 w-2.5" /> Nouveau
            </span>
          </div>
          <div className="px-4 pb-4 pt-1">
            <div className="text-sm font-semibold text-slate-900 leading-snug">Junago Copilot, l'IA qui pilote avec vous.</div>
            <p className="mt-1 text-xs text-slate-500 leading-relaxed">Vos chiffres analysés, vos tendances expliquées — en français.</p>
            <span className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-violet-600">
              Découvrir <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </button>
      </div>
    </div>
  );
}

function PricingPanel({ go }) {
  return (
    <div className="flex flex-col sm:flex-row">
      <div className="p-5 md:p-6 flex-1">
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-violet-600 px-3 mb-3">Tarifs</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <MenuLink icon={Tag} label="Voir les tarifs" sub="Des formules simples, sans surprise" onClick={() => go("/tarifs")} testid="mega-pricing-plans" />
          <MenuLink icon={Gift} label="Essai gratuit 30 jours" sub="Toutes les fonctionnalités, sans carte" onClick={() => go("/register?context=vendor")} testid="mega-pricing-trial" />
          <MenuLink icon={Mail} label="Parler à l'équipe" sub="Un devis adapté à votre flotte" onClick={() => { window.location.href = "mailto:support@junago.fr"; }} testid="mega-pricing-contact" />
        </div>
      </div>
      <div className="p-3 sm:pl-0 flex">
        <VisualCard
          go={go}
          to="/tarifs"
          img={IMG_PRICING}
          overline="Transparence"
          title="Voir tous les tarifs, pensés pour les loueurs."
          testid="mega-pricing-visual"
        />
      </div>
    </div>
  );
}

function ResourcesPanel({ go }) {
  return (
    <div className="flex flex-col sm:flex-row">
      <div className="p-5 md:p-6 flex-1">
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-violet-600 px-3 mb-3">Ressources</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          <div>
            <MenuLink icon={LifeBuoy} label="Centre d'assistance" sub="Guides et documentation complète" onClick={() => go("/assistance")} testid="mega-resources-help" />
            <MenuLink icon={HelpCircle} label="Questions fréquentes" sub="Les réponses aux questions des loueurs" onClick={() => go("/assistance#faq")} testid="mega-resources-faq" />
            <MenuLink icon={BookOpen} label="Cas d'usage" sub="Découvrez Junago en situation" onClick={() => go("/cas-d-usage")} testid="mega-resources-use-cases" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 px-3 mb-1.5">Articles populaires</div>
            {RESOURCE_ARTICLES.map((a) => (
              <button
                key={a.slug}
                onClick={() => go(`/assistance/article/${a.slug}`)}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-slate-100/80 transition-colors text-left"
                data-testid={`mega-resources-article-${a.slug}`}
              >
                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" strokeWidth={1.75} />
                <span className="text-[13px] text-slate-700">{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="p-3 sm:pl-0 flex">
        <VisualCard
          go={go}
          to="/assistance"
          img={IMG_RESOURCES}
          overline="Besoin d'aide ?"
          title="Le centre d'assistance, en français."
          testid="mega-resources-visual"
        />
      </div>
    </div>
  );
}

// Architecture évolutive : ajouter ici les futures entrées (Démonstration, Presse, Carrières, Partenaires)
const COMPANY_LINKS = [
  { icon: Compass, label: "Pourquoi Junago", sub: "Notre vision du métier de loueur", to: "/pourquoi-junago", testid: "mega-company-why" },
  { icon: Map, label: "Roadmap", sub: "Ce qui arrive dans le produit", to: "/assistance/roadmap", testid: "mega-company-roadmap" },
  { icon: Mail, label: "Contact", sub: "Parlons de votre projet", to: "/contact", testid: "mega-company-contact" },
];

const IMG_COMPANY = "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/91f959f8285f7c2e5ded63c872f1cf43d807e830f6f9479e1f0d8fb232c00298.png";

function CompanyPanel({ go }) {
  return (
    <div className="flex flex-col sm:flex-row">
      <div className="p-5 md:p-6 flex-1">
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-violet-600 px-3 mb-3">Entreprise</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          {COMPANY_LINKS.map((l) => (
            <MenuLink key={l.to} icon={l.icon} label={l.label} sub={l.sub} onClick={() => go(l.to)} testid={l.testid} />
          ))}
        </div>
      </div>
      <div className="p-3 sm:pl-0 flex">
        <VisualCard
          go={go}
          to="/pourquoi-junago"
          img={IMG_COMPANY}
          overline="Manifeste"
          title="Pourquoi Junago existe — et où nous allons."
          testid="mega-company-visual"
        />
      </div>
    </div>
  );
}

// ─── Mobile menu (drill-down façon Stripe) ───

const MOBILE_PANELS = {
  solutions: { label: "Solutions", Panel: SolutionsPanel },
  features: { label: "Fonctionnalités", Panel: FeaturesPanel },
  pricing: { label: "Tarifs", Panel: PricingPanel },
  resources: { label: "Ressources", Panel: ResourcesPanel },
  company: { label: "Entreprise", Panel: CompanyPanel },
};

function MobileMenu({ go, user, demo }) {
  const [view, setView] = React.useState("root");
  const loggedTarget = user?.role === "vendor" ? "/vendor/pilotage" : user ? "/mes-reservations" : "/login?context=vendor";
  const loggedLabel = user?.role === "vendor" ? "Accéder à mon espace" : user ? "Mes réservations" : "Se connecter";

  if (view !== "root") {
    const { label, Panel } = MOBILE_PANELS[view];
    return (
      <div data-testid={`mobile-panel-${view}`} style={{ animation: "slideInRight 220ms ease-out both" }}>
        <div className="flex items-center gap-1.5 px-4 pt-4">
          <button
            onClick={() => setView("root")}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
            data-testid="mobile-back-btn"
          >
            <ChevronLeft className="h-4 w-4" /> Retour
          </button>
          <span className="text-sm font-semibold text-slate-400">· {label}</span>
        </div>
        <Panel go={go} demo={demo} />
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="mobile-menu">
      {Object.entries(MOBILE_PANELS).map(([id, { label }]) => (
        <button
          key={id}
          onClick={() => setView(id)}
          className="w-full flex items-center justify-between px-3.5 py-3.5 rounded-xl text-slate-800 hover:bg-slate-100/80 transition-colors text-left"
          data-testid={`mobile-nav-${id}`}
        >
          <span className="text-[15px] font-semibold">{label}</span>
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </button>
      ))}
      <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
        <button onClick={() => go("/register?context=vendor")} className="btn-primary w-full h-11 inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold" data-testid="mobile-trial">
          <Gift className="h-4 w-4" /> Essai gratuit 30 jours
        </button>
        <button onClick={() => go(loggedTarget)} className="btn-jn-secondary w-full h-11 inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold" data-testid="mobile-login">
          <LogIn className="h-4 w-4" /> {loggedLabel}
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───

const NAV_ITEMS = [
  { id: "solutions", label: "Solutions", Panel: SolutionsPanel },
  { id: "features", label: "Fonctionnalités", Panel: FeaturesPanel },
  { id: "pricing", label: "Tarifs", Panel: PricingPanel },
  { id: "resources", label: "Ressources", Panel: ResourcesPanel },
  { id: "company", label: "Entreprise", Panel: CompanyPanel },
];

export default function PublicNavbar({ transparent = false, dark = false }) {
  const { user } = useAuth();
  const [openMenu, setOpenMenu] = React.useState(null);
  const [hovered, setHovered] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const closeTimer = useRef(null);
  const headerRef = useRef(null);
  const containerRef = useRef(null);
  const mobileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);

  useEffect(() => {
    const handler = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpenMenu(null);
      if (mobileRef.current && !mobileRef.current.contains(e.target)) setMobileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    const esc = (e) => { if (e.key === "Escape") { setOpenMenu(null); setMobileOpen(false); } };
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, []);

  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenMenu(null), 160);
  };

  const go = (to) => {
    setOpenMenu(null);
    setMobileOpen(false);
    navigate(to);
  };

  const ActivePanel = NAV_ITEMS.find((n) => n.id === openMenu)?.Panel;
  const onDark = dark && !openMenu;
  const demoAction = () => {
    setOpenMenu(null);
    setMobileOpen(false);
    openInteractiveDemo();
  };

  // Mode landing (junago.io) : en-tête minimal (logo seul), sans navigation ni
  // boutons de connexion/inscription publics.
  if (isLandingMode()) {
    return (
      <header className="sticky top-0 z-50" data-testid="public-navbar-landing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-3">
          <div className="h-14 flex items-center">
            <Link to="/" aria-label="Junago">
              <JunagoLogo size={32} tone="dark" />
            </Link>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      <header
        ref={headerRef}
        className="sticky top-0 z-50"
        data-testid="public-navbar"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-5 pt-2">
          <div
            ref={containerRef}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={`relative h-14 rounded-2xl px-3 sm:px-4 flex items-center gap-6 transition-[background-color,border-color,box-shadow] duration-200 ${
              openMenu
                ? "bg-white rounded-b-none"
                : dark
                ? hovered
                  ? "bg-slate-900/85 backdrop-blur-xl border border-white/15 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.65)]"
                  : "bg-slate-950/60 backdrop-blur-xl border border-white/10 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.55)]"
                : hovered
                ? "bg-white border border-slate-200/60 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.18)]"
                : transparent && !scrolled
                ? "bg-transparent"
                : "glass border border-slate-200/60 shadow-sm"
            }`}
          >
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link" onClick={() => { setOpenMenu(null); setMobileOpen(false); }}>
            <JunagoLogo size={34} tone={onDark ? "light" : "dark"} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
            {NAV_ITEMS.map((n) => (
              <button
                key={n.id}
                onClick={() => setOpenMenu(openMenu === n.id ? null : n.id)}
                onMouseEnter={() => { cancelClose(); setOpenMenu(n.id); }}
                onMouseLeave={scheduleClose}
                className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                  openMenu === n.id
                    ? "text-violet-700"
                    : onDark
                    ? "text-white/85 hover:text-white"
                    : "text-slate-700 hover:text-slate-900"
                }`}
                data-testid={`nav-${n.id}`}
                aria-expanded={openMenu === n.id}
              >
                {n.label}
                <ChevronDown className={`h-3.5 w-3.5 ${onDark ? "text-white/50" : "text-slate-400"} transition-transform duration-200 ${openMenu === n.id ? "rotate-180" : ""}`} />
              </button>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <Link
              to={user?.role === "vendor" ? "/vendor/pilotage" : "/login?context=vendor"}
              className={`hidden lg:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                onDark ? "text-white/85 hover:text-white" : "text-slate-700 hover:text-slate-900"
              }`}
              data-testid="navbar-login-btn"
            >
              {user?.role === "vendor" ? "Mon espace" : "Se connecter"}
            </Link>
            <Link
              to="/register?context=vendor"
              className="hidden lg:inline-flex btn-primary h-9 items-center gap-1.5 px-4 rounded-full text-xs font-semibold"
              data-testid="navbar-trial-btn"
            >
              Essai gratuit 30 jours <ArrowRight className="h-3 w-3" />
            </Link>
            {/* Mobile burger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden h-11 w-11 rounded-full border flex items-center justify-center transition-colors ${
                onDark ? "border-white/20 bg-white/10 hover:border-white/45" : "border-slate-200 bg-white hover:border-slate-400"
              }`}
              data-testid="burger-menu-btn"
              aria-label="Menu"
            >
              {mobileOpen ? <X className={`h-5 w-5 ${onDark ? "text-white" : "text-slate-700"}`} /> : <Menu className={`h-5 w-5 ${onDark ? "text-white" : "text-slate-700"}`} />}
            </button>
          </div>

          {/* Desktop mega menu panel — pleine largeur du header, fusionné */}
          {ActivePanel && (
            <div
              className="hidden lg:block absolute top-full left-0 right-0"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              data-testid={`mega-panel-${openMenu}`}
            >
              <div className="bg-white rounded-b-2xl shadow-[0_35px_70px_-15px_rgba(15,23,42,0.28)] border-t border-slate-100 overflow-hidden" style={{ animation: "fadeInUp 200ms ease-out both" }}>
                <ActivePanel go={go} demo={demoAction} />
              </div>
            </div>
          )}
        </div>
        </div>
      </header>

      {/* Flou d'arrière-plan (desktop) quand un mega menu est ouvert */}
      <div
        className={`hidden lg:block fixed inset-0 z-40 bg-white/40 backdrop-blur-[6px] transition-opacity duration-300 ${openMenu ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={() => setOpenMenu(null)}
        data-testid="mega-menu-backdrop"
        aria-hidden
      />

      {/* Mobile overlay + panel */}
      <div className={`lg:hidden fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-[2px] transition-opacity duration-200 ${mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} onClick={() => setMobileOpen(false)} />
      <div
        ref={mobileRef}
        className={`lg:hidden fixed z-50 top-[72px] right-4 w-[calc(100vw-2rem)] max-w-[400px] transition-all duration-250 origin-top-right ${mobileOpen ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-[0.97] -translate-y-2 pointer-events-none"}`}
        data-testid="mobile-menu-wrap"
      >
        <div className="bg-white rounded-2xl shadow-[0_25px_60px_-12px_rgba(15,23,42,0.25)] border border-slate-200/80 overflow-hidden max-h-[calc(100vh-96px)] overflow-y-auto">
          <MobileMenu key={mobileOpen ? "open" : "closed"} go={go} user={user} demo={demoAction} />
        </div>
      </div>
    </>
  );
}
